import React, { useState } from 'react';
import { Wizard, WizardProps } from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { NameAndDescriptionEditor } from 'src/components/auditReports/createReportPage/nameAndDescriptionEditor';
import {
  AccessGrantConfigEditor,
  resourceArnIsValid,
} from 'src/components/auditReports/createReportPage/accessGrantConfigEditor';
import { ReviewAndSubmit } from 'src/components/auditReports/createReportPage/reviewAndSubmit';

export interface CreateReportWizardProps {
  name: string;
  setName: any;
  description: string;
  setDescription: any;
  outputFormats: string[];
  setOutputFormats: any;
  accessProvider: string;
  setAccessProvider: any;
  governanceTag: string;
  setGovernanceTag: any;
  resourceArn: string;
  setResourceArn: any;
  status: string;
  setStatus: any;
  startDate: string;
  setStartDate: any;
  endDate: string;
  setEndDate: any;
  handleCancel: any;
  handleSubmit: any;
  loading: boolean;
}

export interface StepErrors {
  name?: string;
  description?: string;
  outputFormats?: string;
  resourceArn?: string;
}

export const CreateReportWizard = (props: CreateReportWizardProps) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [stepErrors, setStepErrors] = useState<StepErrors>({});

  const WIZARD_STEPS: WizardProps.Step[] = [
    {
      title: 'Name and description',
      info: <Link variant='info'>Info</Link>,
      description:
        'Provide a name and description for the report. This will only be used for your own reference. Then, select output format(s) for the report.',
      content: (
        <NameAndDescriptionEditor
          name={props.name}
          setName={props.setName}
          description={props.description}
          setDescription={props.setDescription}
          outputFormats={props.outputFormats}
          setOutputFormats={props.setOutputFormats}
          stepErrors={stepErrors}
          validateFieldErrorsOnChange={(fieldName: string, fieldValue: string) =>
            validateCurrentStringStepField(fieldName, fieldValue)
          }
        />
      ),
    },
    {
      title: 'Access grant config',
      isOptional: true,
      info: <Link variant='info'>Info</Link>,
      description: 'Set optional filters to configure what access grants will be included in the report.',
      content: (
        <AccessGrantConfigEditor
          {...props}
          accessProvider={props.accessProvider}
          setAccessProvider={props.setAccessProvider}
          governanceTag={props.governanceTag}
          setGovernanceTag={props.setGovernanceTag}
          resourceArn={props.resourceArn}
          setResourceArn={props.setResourceArn}
          status={props.status}
          setStatus={props.setStatus}
          startDate={props.startDate}
          setStartDate={props.setStartDate}
          endDate={props.endDate}
          setEndDate={props.setEndDate}
          displayAccessProviderFilter={true}
          displayDecisionDateRange={false}
          stepErrors={stepErrors}
          validateResourceArnStepField={(resourceArn) => validateResourceArnStepField(resourceArn)}
        />
      ),
    },
    {
      title: 'Review and submit',
      info: <Link variant='info'>Info</Link>,
      description: 'Review the report configuration below, then submit to start report generation.',
      content: (
        <ReviewAndSubmit
          name={props.name}
          setName={props.setName}
          description={props.description}
          setDescription={props.setDescription}
          outputFormats={props.outputFormats}
          setOutputFormats={props.setOutputFormats}
          accessProvider={props.accessProvider}
          setAccessProvider={props.setAccessProvider}
          governanceTag={props.governanceTag}
          setGovernanceTag={props.setGovernanceTag}
          resourceArn={props.resourceArn}
          setResourceArn={props.setResourceArn}
          status={props.status}
          setStatus={props.setStatus}
          startDate={props.startDate}
          setStartDate={props.setStartDate}
          endDate={props.endDate}
          setEndDate={props.setEndDate}
        />
      ),
    },
  ];

  // Validate current step on navigation
  const validateCurrentStringStepField = (fieldName: string, fieldValue: string) => {
    if (!fieldValue || fieldValue?.trim() === '') {
      setStepErrors((currentStepErrors) => ({ ...currentStepErrors, [fieldName]: 'This field is required.' }));
    } else {
      setStepErrors((currentStepErrors) => ({ ...currentStepErrors, [fieldName]: undefined }));
    }
  };

  const validateResourceArnStepField = (resourceArn: string) => {
    if (resourceArnIsValid(resourceArn)) {
      setStepErrors((currentStepErrors) => ({ ...currentStepErrors, resourceArn: undefined }));
    } else {
      setStepErrors((currentStepErrors) => ({
        ...currentStepErrors,
        resourceArn: 'Resource ARN prefix is not valid!',
      }));
    }
  };

  // Validate current step on navigation
  const validateCurrentStep = () => {
    const currentStepErrors: StepErrors = {};
    let isValid = true;

    // Name and description step
    if (activeStepIndex === 0) {
      // Validate name and description
      if (!props.name || props.name.trim() === '') {
        currentStepErrors.name = 'Name is required and must be between 1 and 256 characters.';
        isValid = false;
      }
      // Validate description
      if (!props.description || props.description.trim() === '') {
        currentStepErrors.description = 'Description for the report is required!';
        isValid = false;
      }
      // Validate output format
      if (!props.outputFormats || props.outputFormats.length == 0) {
        currentStepErrors.outputFormats = 'Output format for the report is required!';
        isValid = false;
      }
    } else if (activeStepIndex === 1) {
      // Validate configs - resource Arn
      if (!resourceArnIsValid(props.resourceArn)) {
        currentStepErrors.resourceArn = 'Resource ARN prefix is not valid!';
        isValid = false;
      }
    }

    if (!isValid) {
      setStepErrors(currentStepErrors);
    } else {
      setStepErrors({});
    }
    return isValid;
  };

  // Handle navigation between steps
  const handleNavigate = ({ detail }) => {
    const targetStep = detail.requestedStepIndex;

    // Only validate when moving forward
    if (targetStep > activeStepIndex) {
      const isValid = validateCurrentStep();
      if (!isValid) return;
    }

    // If validation passes, move to the requested step
    setActiveStepIndex(targetStep);
  };

  const WIZARD_I18N_STRINGS = {
    stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
    collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
    skipToButtonLabel: (step) => `Skip to ${step.title}`,
    navigationAriaLabel: 'Steps',
    cancelButton: 'Cancel',
    previousButton: 'Previous',
    nextButton: 'Next',
    submitButton: 'Submit',
    optional: 'optional',
  };

  return (
    <Wizard
      steps={WIZARD_STEPS}
      i18nStrings={WIZARD_I18N_STRINGS}
      activeStepIndex={activeStepIndex}
      onNavigate={handleNavigate}
      onCancel={props.handleCancel}
      onSubmit={props.handleSubmit}
      isLoadingNextStep={props.loading}
      allowSkipTo={false}
    />
  );
};
